import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import { useGoogleDriveCache } from "@/lib/contexts/GoogleDriveCacheContext";

import {
  buildAuthHeaders,
  invalidateAfterMutation,
  parseDriveErrorMessage,
} from "./google-drive-browser-internals";

/**
 * Hook for creating folders in Google Drive
 */
export const useCreateGoogleDriveFolder = () => {
  const { session } = useUniversalAuth();
  const queryClient = useQueryClient();
  const cache = useGoogleDriveCache();

  return useMutation({
    mutationFn: async (params: {
      folderName: string;
      parentId?: string;
      driveId?: string;
      isSecondAccount?: boolean;
      action?: "list" | "drives";
    }) => {
      const token = session?.access_token;
      if (!token) {
        throw new Error("No authentication token");
      }

      const response = await fetch("/api/unifiedtime/getDrive/createFolder", {
        method: "POST",
        headers: buildAuthHeaders(token),
        body: JSON.stringify({
          ...params,
          action: params.action || "list",
        }),
      });

      if (!response.ok) {
        throw new Error(
          await parseDriveErrorMessage(
            response,
            `Failed to create folder: ${response.statusText}`
          )
        );
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      invalidateAfterMutation(cache, variables);

      const cacheKey = [
        "google-drive-browser",
        {
          action: "list",
          parentId: variables.parentId,
          driveId: variables.driveId,
          email: undefined,
          isSecondAccount: variables.isSecondAccount || false,
          pageToken: undefined,
          pageSize: undefined,
        },
      ];
      void queryClient.invalidateQueries({ queryKey: cacheKey });
    },
  });
};

/**
 * Hook for uploading files to Google Drive
 */
export const useUploadGoogleDriveFile = () => {
  const { session } = useUniversalAuth();
  const cache = useGoogleDriveCache();

  return useMutation({
    mutationFn: async (params: {
      file: File;
      parentId?: string;
      driveId?: string;
      isSecondAccount?: boolean;
    }) => {
      const token = session?.access_token;
      if (!token) {
        throw new Error("No authentication token");
      }

      const formData = new FormData();
      formData.append("file", params.file);
      if (params.parentId) formData.append("parentId", params.parentId);
      if (params.driveId) formData.append("driveId", params.driveId);
      if (params.isSecondAccount) formData.append("isSecondAccount", "true");

      const response = await fetch("/api/unifiedtime/getDrive/uploadFile", {
        method: "POST",
        headers: buildAuthHeaders(token, false),
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          await parseDriveErrorMessage(
            response,
            `Failed to upload file: ${response.statusText}`
          )
        );
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      invalidateAfterMutation(cache, variables);
    },
  });
};

/**
 * Hook for deleting (moving to Trash) a Google Drive file.
 *
 * Trashes rather than permanently deletes — recoverable from Drive Trash.
 */
export const useDeleteGoogleDriveFile = () => {
  const { session } = useUniversalAuth();
  const cache = useGoogleDriveCache();

  return useMutation({
    mutationFn: async (params: {
      fileId: string;
      parentId?: string;
      driveId?: string;
      isSecondAccount?: boolean;
    }) => {
      const token = session?.access_token;
      if (!token) {
        throw new Error("No authentication token");
      }

      const response = await fetch("/api/unifiedtime/getDrive/deleteFile", {
        method: "POST",
        headers: buildAuthHeaders(token),
        body: JSON.stringify({
          fileId: params.fileId,
          isSecondAccount: params.isSecondAccount,
        }),
      });

      if (!response.ok) {
        throw new Error(
          await parseDriveErrorMessage(response, `Failed to delete file: ${response.statusText}`)
        );
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      invalidateAfterMutation(cache, variables);
    },
  });
};
